import * as React from "react";
import { useContext } from "react";
import { CacheContext } from "../../../../../contexts/CacheContext";
import Table from "../../../bits/Table/Table";
import { Button } from "../../../bits/Button/Button";
import EditIcon from "../../../icons/Edit";
import TrashIcon from "../../../icons/Trash";
import styles from "./SavedSignatures.module.scss";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import {
    deleteEmailSignature,
    updateEmailSignatureCache,
} from "../../../../../store/email-signatures";
import { useToasts } from "react-toast-notifications";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { Dialog } from "../../../bits/Dialog/Dialog";
import CreateSignature from "../CreateSignature/CreateSignature";
import { SignatureData } from "intake_shared/src/types";

interface TableColumn {
    Header: string;
    id?: string;
    accessor: string | ((row: any) => JSX.Element);
}

export default function SavedSignatures() {
    const { addToast } = useToasts();
    const olc = useContext(CacheContext);
    const signatures = olc?.emailSignatures || {};
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [emailToDelete, setEmailToDelete] = React.useState("");
    const [signatureInView, setSignatureInView] = React.useState(
        {} as SignatureData
    );

    const [editSignature, setEditSignature] = React.useState(false);

    const handleDeleteSignature = async (email: string) => {
        const deleted = await deleteEmailSignature(email);
        const returnedEmailSignatures = await updateEmailSignatureCache();
        if (returnedEmailSignatures) {
            olc?.updateEmailSignatures(returnedEmailSignatures);
        }
        if (deleted) {
            addToast(`Signature deleted`, {
                appearance: "success",
                autoDismiss: true,
            });
        } else {
            addToast(`An error occured`, {
                appearance: "error",
                autoDismiss: true,
            });
        }
        setShowDeleteDialog(false);
    };

    const columns = React.useMemo((): TableColumn[] => {
        return [
            {
                Header: "Full Name",
                id: "name",
                accessor: (row: SignatureData) => <span>{row.name || "--"}</span>,
            },
            {
                Header: "Email",
                id: "email",
                accessor: (row: SignatureData) => <span>{row.email || "--"}</span>,
            },
            {
                Header: "Company",
                id: "company",
                accessor: (row: SignatureData) => <span>{row.company || "--"}</span>,
            },
            {
                Header: "Department",
                id: "department",
                accessor: (row: SignatureData) => (
                    <span>{row.department || "--"}</span>
                ),
            },
            {
                Header: "Office Phone",
                id: "phone",
                accessor: (row: SignatureData) => <span>{row.phone || "--"}</span>,
            },
            {
                Header: "Direct Phone",
                id: "directPhone",
                accessor: (row: SignatureData) => (
                    <span>{row.directPhone || "--"}</span>
                ),
            },
            {
                Header: "Pronouns",
                id: "pronouns",
                accessor: (row: SignatureData) => (
                    <span>{row.pronouns || "--"}</span>
                ),
            },
            {
                Header: "Include Disclaimer",
                id: "includeDisclaimer",
                accessor: (row: SignatureData) => (
                    <span>{row.includeDisclaimer ? "Yes" : "No"}</span>
                ),
            },
            {
                Header: "",
                id: "actions",
                accessor: (row: SignatureData) => (
                    <div className={styles.actionButtons}>
                        <Button
                            variant="normal"
                            onClick={() => {
                                setSignatureInView(row);
                                setEditSignature(true);
                            }}
                            className={styles.editButton}
                        >
                            <EditIcon stroke="var(--color-gray)" />
                        </Button>
                        <Button
                            variant="danger"
                            mode="outline"
                            size="icon"
                            title="Delete signature"
                            aria-label="Delete signature"
                            onClick={() => {
                                setEmailToDelete(row.email);
                                setShowDeleteDialog(true);
                            }}
                        >
                            <TrashIcon stroke="currentColor" />
                        </Button>
                    </div>
                ),
            },
        ];
    }, []);

    const signaturesArray = Object.values(signatures);

    return (
        <div className={styles.container}>
            <Dialog
                open={editSignature}
                onClose={() => {
                    setEditSignature(false);
                    setSignatureInView({} as SignatureData);
                }}
                title={`Edit ${signatureInView?.name}'s signature`}
                wrapperClassName={styles.dialog}
                showFooter={false}
            >
                <CreateSignature
                    mode="edit"
                    signatureInView={signatureInView}
                    setEditSignature={setEditSignature}
                />
            </Dialog>
            <AlertDialog
                open={showDeleteDialog}
                onSaveClick={async () => {
                    handleDeleteSignature(emailToDelete);
                }}
                onClose={() => setShowDeleteDialog(false)}
                title="Delete signature?"
                isDeleting
                saveText="Delete"
                description={
                    <div>
                        Are you sure you want to delete this email signature? This
                        action cannot be reversed.
                    </div>
                }
            />
            {signaturesArray.length === 0 ? (
                <EmptyListState
                    emptyMessage="No signatures saved yet"
                    stateHelperText="Signatures will show here when added."
                />
            ) : (
                <Table
                    stickyLastColumn
                    header={<p className={styles.header}>Saved Signatures</p>}
                    columns={columns}
                    data={signaturesArray}
                    allowSelect={false}
                />
            )}
        </div>
    );
}
